import type { ReactNode } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { Menu } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'
import { useAdminMenu } from './menu'

interface AdminScreenProps {
  title: string
  subtitle: string
  actions?: ReactNode
  children: ReactNode
}

export function AdminScreen({
  title,
  subtitle,
  actions,
  children,
}: AdminScreenProps) {
  const { isMenuOpen } = useAdminMenu()

  return (
    <ScrollView
      className="flex-1"
      contentContainerClassName="grow px-6 py-8 lg:px-6 lg:py-10 xl:px-8"
      scrollEnabled={!isMenuOpen}
    >
      <View className="w-full max-w-[1280px] self-center">
        <AdminPageHeader title={title} subtitle={subtitle} actions={actions} />

        <View className="mt-8">{children}</View>
      </View>
    </ScrollView>
  )
}

interface AdminPageHeaderProps {
  title: string
  subtitle: string
  actions?: ReactNode
}

function AdminPageHeader({ title, subtitle, actions }: AdminPageHeaderProps) {
  const { t } = useTranslation()
  const { onOpenMenu } = useAdminMenu()

  return (
    <View>
      <View className="flex-row items-center justify-between gap-4">
        <View className="min-w-0 flex-1 flex-row items-center gap-3">
          {onOpenMenu ? (
            <Pressable
              onPress={onOpenMenu}
              accessibilityLabel={t('nav.openMenu')}
              accessibilityRole="button"
              className="shrink-0"
            >
              <Menu size={28} color={colors.text.primary} strokeWidth={2} />
            </Pressable>
          ) : null}
          <Text className="min-w-0 flex-1 text-3xl font-bold text-primary" numberOfLines={1}>{title}</Text>
        </View>
        {actions ? <View className="shrink-0 lg:max-w-[420px]">{actions}</View> : null}
      </View>
      <Text className="mt-2 text-base font-normal text-secondary">{subtitle}</Text>
    </View>
  )
}
